import React from 'react';

import { Event } from '../../shared/types/event';
import { GridEventStyle, GridEventPos, GridEventMeta } from './GridEvent';
import { GridEvent, GridGroupEvent } from '.';
import { GroupEvent } from '../../shared/types/groupEvent';
import { timeToMinutes } from '../../shared/lib/time';
import { Time } from '@go-shared/types/time';
import { BLOCK_LENGTH, GridScrollPosition } from './Grid';

import './GridBody.scss';
import { VenueDispositionType } from '@go-shared/types/plan';
import { PropertyType } from '@go-shared/types/property';
import { BrandMealPeriod } from '@go-shared/lib/mealPeriods';
import { VenueTemplate } from '@go-shared/types/venueTemplate';
import EventsGlance from './EventsGlance';
import { BubbleButton } from './BubbleUI';
import { evaluateMcmTruthyAggregate, toggleMcmEndpoint } from '@go-shared/lib/mcm';
import { BrandCommonTextValues, HourFormat } from '@go-shared/types/brand';

export interface EventElement {
  key: string;
  event: Event;
  style: GridEventStyle;
  meta: GridEventMeta;
}

export interface GroupEventElement {
  key: string;
  groupEvent: GroupEvent;
  style: GridEventStyle;
  meta: GridEventMeta;
  setUpTimeStart?: number;
  breakdownTimeEnd?: number;
}

export interface VenueElement {
  venue: VenueTemplate;
  style: VenueStyle;
  eventEls: EventElement[];
  groupEventEls: GroupEventElement[];
  dispositionEls: VenueDataStyle[];
  meta: {
    trackStart: number;
    trackSize: number;
  };
}

export interface VenueStyle {
  width: number;
  maxWidth: number;
}

export interface VenueDataStyle {
  top: number;
  left: number;
  width: number;
  height: number;
  color: string;
  type: VenueDispositionType;
}

export interface GridBodyProps {
  commonTexts: BrandCommonTextValues;
  mcmEndpoints: string[];
  hourFormat: HourFormat;
  eventEls: EventElement[];
  groupEventEls: GroupEventElement[];
  venueEls: VenueElement[];
  venueData: VenueDataStyle[];
  audiences: string[];
  width: number;
  height: number;
  locked: boolean;
  levelLabel: string;
  cellWidth: number;
  cellHeight: number;
  departureTimes?: Time[];
  arrivalTimes?: Time[];
  propertyType: PropertyType;
  dayStart?: Time;
  venueDisplayType: VenueDispositionType;
  scrollPostion: GridScrollPosition;
  mealPeriods: BrandMealPeriod[];
  onDoubleClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onEventMove?: (event: Event, to: GridEventPos, from: GridEventPos) => void;
  onEventResize?: (event: Event, height: number) => void;
  onEventEdit?: (event: Event) => void;
  onToggleEventHiddenClick?: (event: Event) => void;
  onEventDelete?: (eventId: string) => void;
  onStaffAllocationEditClick?: (event: Event) => void;
  onIsBusy?: (isDragging: boolean) => void;
  onUpdateEvent?: (event: Event) => void;
}

export interface GridBodyState {
  displayedStaffEvent?: Event & GridEventStyle;
  showDeletePrompt: boolean;
  busy: boolean;
}

export default class GridBody extends React.Component<GridBodyProps, GridBodyState> {
  public readonly state: GridBodyState = { busy: false, showDeletePrompt: false };

  private hideTimeoutHandler?: NodeJS.Timeout;

  constructor(props: GridBodyProps) {
    super(props);

    this.handleStaffMouseEnter = this.handleStaffMouseEnter.bind(this);
    this.handleStaffMouseLeave = this.handleStaffMouseLeave.bind(this);
    this.handleStaffPopupMouseEnter = this.handleStaffPopupMouseEnter.bind(this);
    this.handleStaffPopupMouseLeave = this.handleStaffPopupMouseLeave.bind(this);
    this.handleStaffEditClick = this.handleStaffEditClick.bind(this);
    this.handleOnIsBusy = this.handleOnIsBusy.bind(this);
  }

  public render() {
    let backgroundColor = this.props.locked ? '#f9f9f9' : '#f5faff';
    let cellColor = this.props.locked ? '#e5e5e5' : '#dee6ed';

    let widthMinusBorders = this.props.cellWidth - 2;
    let widthOffsetOne = this.props.cellWidth - 1;

    let heightMinusBorders = this.props.cellHeight - 2;
    let heightOffsetOne = this.props.cellHeight - 1;

    let backgroundImage =
      `url("data:image/svg+xml,` +
      `%3Csvg xmlns='http://www.w3.org/2000/svg' width='${this.props.cellWidth}' ` +
      `height='${this.props.cellHeight}' viewBox='0 0 ${this.props.cellWidth} ${this.props.cellHeight}'%3E` +
      `%3Crect fill='transparent' x='1' y='1' width='${widthMinusBorders}' height='${heightMinusBorders}' /%3E` +
      `%3Crect fill='transparent' x='0' y='0' width='1' height='1' /%3E` +
      `%3Crect fill='transparent' x='1' y='0' width='${widthMinusBorders}' height='1' /%3E` +
      `%3Crect fill='%23${cellColor.substring(
        1
      )}' x='${widthOffsetOne}' y='0' width='1' height='1' /%3E` +
      `%3Crect fill='%23${cellColor.substring(
        1
      )}' x='${widthOffsetOne}' y='1' width='1' height='${heightMinusBorders}' /%3E` +
      `%3Crect fill='%23${cellColor.substring(
        1
      )}' x='${widthOffsetOne}' y='${heightOffsetOne}' width='1' height='1' /%3E` +
      `%3Crect fill='%23${cellColor.substring(
        1
      )}' x='1' y='${heightOffsetOne}' width='${widthMinusBorders}' height='1' /%3E` +
      `%3Crect fill='%23${cellColor.substring(
        1
      )}' x='0' y='${heightOffsetOne}' width='1' height='1' /%3E` +
      `%3Crect fill='transparent' x='0' y='1' width='1' height='${heightMinusBorders}' /%3E` +
      `%3C/svg%3E` +
      `%0A")`;

    const mergeLines = this.calculateMergeLines(this.props.venueEls);

    const isVisible = this.state.displayedStaffEvent
      ? evaluateMcmTruthyAggregate(
          'visible',
          this.props.mcmEndpoints,
          this.state.displayedStaffEvent?.mcm,
          this.state.displayedStaffEvent?.template.mcm
        )
      : undefined;

    const isTrending = this.state.displayedStaffEvent
      ? evaluateMcmTruthyAggregate(
          'trending',
          this.props.mcmEndpoints,
          this.state.displayedStaffEvent?.mcm,
          this.state.displayedStaffEvent?.template.mcm
        )
      : undefined;

    return (
      <div
        id="GridBody"
        className="GridBody"
        onDoubleClick={
          !this.props.locked && this.props.onDoubleClick ? this.props.onDoubleClick : undefined
        }
        style={{
          width: this.props.width,
          height: this.props.height,
          backgroundImage,
          backgroundColor,
        }}
      >
        {mergeLines.map((ml, i) => (
          <div
            key={`merge-${i}`}
            className="absolute"
            style={{
              width: '3px',
              height: this.props.height,
              left: ml - 1, // (width to 3) - 1 centers the bar
              backgroundImage,
              backgroundColor,
            }}
          />
        ))}
        {this.props.arrivalTimes &&
          this.props.arrivalTimes.map((arrivalTime, i) => (
            <LimitLine
              key={`arrival-${i}`}
              gridWidth={this.props.width}
              cellHeight={this.props.cellHeight}
              dayStart={this.props.dayStart ? this.props.dayStart : { hour: 6, minute: 0 }}
              time={arrivalTime}
              lineType="arrival"
            />
          ))}
        {this.props.departureTimes &&
          this.props.departureTimes.map((departureTime, i) => (
            <LimitLine
              key={`departure-${i}`}
              gridWidth={this.props.width}
              cellHeight={this.props.cellHeight}
              dayStart={this.props.dayStart ? this.props.dayStart : { hour: 6, minute: 0 }}
              time={departureTime}
              lineType="departure"
            />
          ))}
        {this.props.eventEls.map(evtEl => (
          <GridEvent
            key={evtEl.key}
            commonTexts={this.props.commonTexts}
            hourFormat={this.props.hourFormat}
            eventEl={evtEl}
            audiences={this.props.audiences}
            mcmEndpoints={this.props.mcmEndpoints}
            locked={this.props.locked}
            scrollPosition={this.props.scrollPostion}
            onMove={this.props.onEventMove}
            onResize={this.props.onEventResize}
            onDoubleClick={this.props.onEventEdit}
            onToggleEventHiddenClick={this.props.onToggleEventHiddenClick}
            onStaffMouseEnter={this.handleStaffMouseEnter}
            onStaffMouseLeave={this.handleStaffMouseLeave}
            onIsBusy={this.handleOnIsBusy}
          />
        ))}
        {this.props.groupEventEls.map(gEvtEl => (
          <GridGroupEvent {...gEvtEl} />
        ))}
        {this.props.venueData
          .filter(vd => vd.type === this.props.venueDisplayType)
          .map((block, i) => (
            <div
              key={`venue-${i}`}
              className="VenueBlock absolute select-none"
              style={{ ...block, backgroundColor: block.color, zIndex: 9 }}
            >
              {block.type === 'mealPeriods' && (
                <div
                  className="text-xs flex items-center justify-center
                                text-white font-bold mt-2"
                >
                  {this.getMealPeriodNameByColor(block.color, this.props.mealPeriods)}
                </div>
              )}
            </div>
          ))}

        {this.state.displayedStaffEvent && (
          <div
            style={this.getStaffPopupStyle(this.state.displayedStaffEvent)}
            className={`z-50 overflow-y-auto relative shadow-lg bg-white border p-3`}
            onMouseEnter={this.handleStaffPopupMouseEnter}
            onMouseLeave={this.handleStaffPopupMouseLeave}
          >
            {!this.props.locked && (
              <div className="absolute top-0 right-0 grid grid-cols-1 p-1">
                <BubbleButton
                  block
                  variant="navy"
                  inverse
                  onClick={() => {
                    if (this.state.displayedStaffEvent && this.props.onEventEdit) {
                      this.props.onEventEdit(this.state.displayedStaffEvent);
                    }
                  }}
                  icon="edit"
                />
                <BubbleButton
                  block
                  variant={isVisible ? 'navy' : isVisible === null ? 'banana' : 'smoke'}
                  inverse
                  onClick={() => {
                    if (this.state.displayedStaffEvent && this.props.onUpdateEvent) {
                      const newEvt = {
                        ...this.state.displayedStaffEvent,
                        mcm: toggleMcmEndpoint(
                          'visible',
                          'all',
                          this.props.mcmEndpoints,
                          this.state.displayedStaffEvent?.mcm,
                          this.state.displayedStaffEvent?.template.mcm
                        ),
                      };
                      this.props.onUpdateEvent(newEvt);
                    }
                  }}
                  icon={isVisible ? 'eye' : isVisible === null ? 'eye' : 'eye-slash'}
                />
                <BubbleButton
                  block
                  variant={isTrending ? 'crimson' : isTrending === null ? 'banana' : 'smoke'}
                  inverse
                  onClick={() => {
                    if (this.state.displayedStaffEvent && this.props.onUpdateEvent) {
                      const newEvt = {
                        ...this.state.displayedStaffEvent,
                        mcm: toggleMcmEndpoint(
                          'trending',
                          'all',
                          this.props.mcmEndpoints,
                          this.state.displayedStaffEvent?.mcm,
                          this.state.displayedStaffEvent?.template.mcm
                        ),
                      };
                      this.props.onUpdateEvent(newEvt);
                    }
                  }}
                  icon="fire"
                />
                {!this.state.showDeletePrompt && (
                  <BubbleButton
                    block
                    variant="crimson"
                    inverse
                    onClick={() => {
                      this.setState({ showDeletePrompt: true });
                    }}
                    icon="trash-alt"
                  />
                )}
                {this.state.showDeletePrompt && (
                  <>
                    <BubbleButton
                      block
                      variant="slate"
                      inverse
                      onClick={() => {
                        this.setState({ showDeletePrompt: false });
                      }}
                      icon="trash-xmark"
                    />
                    <BubbleButton
                      block
                      variant="crimson"
                      inverse
                      onClick={() => {
                        if (this.state.displayedStaffEvent && this.props.onEventDelete) {
                          this.props.onEventDelete(this.state.displayedStaffEvent._id);
                          this.setState({
                            displayedStaffEvent: undefined,
                            showDeletePrompt: false,
                          });
                        }
                      }}
                      icon="trash-check"
                    />
                  </>
                )}
              </div>
            )}
            <div className="pr-6">
              <EventsGlance
                locked={this.props.locked}
                event={this.state.displayedStaffEvent}
                onUpdateEvent={this.props.onUpdateEvent}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  public componentDidUpdate(prevProps: Readonly<GridBodyProps>): void {
    if (prevProps.eventEls !== this.props.eventEls) {
      const updatedDisplayedStaffEvt = this.props.eventEls.find(
        evt => evt.event._id === this.state.displayedStaffEvent?._id
      );

      if (updatedDisplayedStaffEvt && this.state.displayedStaffEvent) {
        this.setState({
          showDeletePrompt: false,
          displayedStaffEvent: {
            ...this.state.displayedStaffEvent,
            ...updatedDisplayedStaffEvt.event,
          },
        });
      }
    }
  }

  private calculateMergeLines(venueEls: VenueElement[]) {
    let mergeLines: number[] = [];
    let mergeLineCursor = -1;

    venueEls.forEach(vel => {
      let workingSize = vel.meta.trackSize;

      while (workingSize > 0) {
        mergeLineCursor += this.props.cellWidth;
        workingSize -= 1;
        if (workingSize > 0) {
          mergeLines.push(mergeLineCursor);
        }
      }
    });

    return mergeLines;
  }

  private getStaffPopupStyle(event: Event & GridEventStyle): any {
    return {
      userSelect: 'text',
      top: event.top,
      left: event.left + event.width,
      width: 400,
      height: 400,
      overflowY: 'auto',
    };
  }

  private handleStaffMouseEnter(event: Event, eventStyle: GridEventStyle) {
    if (this.state.busy) return;

    // If we are entering the event staff icon but there's a hide timeout in progress, cancel it
    if (this.hideTimeoutHandler) {
      clearTimeout(this.hideTimeoutHandler);
    }

    this.setState({ displayedStaffEvent: { ...event, ...eventStyle }, showDeletePrompt: false });
  }

  private handleStaffMouseLeave() {
    // we add a slight timeout in case users are moving the mouse out of the event and inside the
    // actual popup. If that happens, we don't want to hide it
    this.hideTimeoutHandler = setTimeout(
      () => this.setState({ displayedStaffEvent: undefined, showDeletePrompt: false }),
      100
    ) as unknown as NodeJS.Timeout;
  }

  private handleStaffPopupMouseEnter() {
    if (this.state.busy) return;

    // If we are entering the popup and there's a current timeout handler, cancel it first
    if (this.state.displayedStaffEvent && this.hideTimeoutHandler) {
      clearTimeout(this.hideTimeoutHandler);
    }
  }

  private handleStaffPopupMouseLeave() {
    this.hideTimeoutHandler = setTimeout(
      () => this.setState({ displayedStaffEvent: undefined, showDeletePrompt: false }),
      100
    ) as unknown as NodeJS.Timeout;
  }

  private handleStaffEditClick(event: Event) {
    this.setState({ displayedStaffEvent: undefined, showDeletePrompt: false });

    if (this.props.onStaffAllocationEditClick) {
      this.props.onStaffAllocationEditClick(event);
    }
  }

  private handleOnIsBusy(busy: boolean) {
    if (this.props.onIsBusy) {
      this.setState({ displayedStaffEvent: undefined, busy, showDeletePrompt: false });
      this.props.onIsBusy(busy);
    }
  }

  private getMealPeriodNameByColor = (color: string, mealPeriods: BrandMealPeriod[]) => {
    const MP = mealPeriods.find(el => el.color === color);
    if (MP) return MP.name;
    return '';
  };
}

const LimitLine = (props: {
  time: Time;
  dayStart: Time;
  gridWidth: number;
  lineType: 'arrival' | 'departure';
  cellHeight: number;
}) => {
  const dayStartInMinutes = timeToMinutes(props.dayStart);

  const intervals = timeToMinutes(props.time, dayStartInMinutes) / BLOCK_LENGTH;
  const yPosition = intervals * props.cellHeight + 'px';
  const lineClasses =
    props.lineType === 'arrival' ? `arrival-line bg-blue-dark` : `departure-line bg-red-dark`;

  return (
    <div
      className={`grid-limit-line ${lineClasses}`}
      style={{ top: yPosition, width: props.gridWidth }}
    />
  );
};
